import * as SelectPrimitive from "@radix-ui/react-select";
import { IconSelector } from "@tabler/icons-react";

import type { ProgramDetails } from "@/services/program.service";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Select, SelectContent, SelectItem } from "@/components/ui/select";
import { formatNumber } from "@/utils/formatters";

/**
 * Which brand the dashboard is showing. Built like the account trigger in
 * the sidebar (logo, name, a second line, a selector glyph) so it reads as
 * a switch rather than a form field, and the second line says how many
 * brands there are to switch between.
 */
export function BrandSwitcher({
  programs,
  value,
  onChange,
}: {
  programs: ProgramDetails[];
  value: string;
  onChange: (id: string) => void;
}) {
  const selected = programs.find((p) => p.id === value);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectPrimitive.Trigger
        aria-label="Switch brand"
        className="group flex h-auto min-w-[15rem] items-center gap-2.5 rounded-lg border border-border bg-card py-1.5 pl-1.5 pr-2.5 text-left transition-colors hover:border-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md">
          <BrandLogo
            src={selected?.brandProfileImage}
            name={selected?.companyName}
            className="size-9 rounded-md"
            initialsClassName="text-sm"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium text-foreground">
            {selected?.companyName ?? "Choose a brand"}
          </span>
          <span className="block truncate text-[11px] text-muted-foreground">
            Switch brand, {formatNumber(programs.length)} to choose from
          </span>
        </span>
        <SelectPrimitive.Icon asChild>
          <IconSelector
            size={16}
            stroke={1.75}
            className="shrink-0 text-muted-foreground transition-colors group-hover:text-brand-600"
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectContent align="end" className="min-w-[15rem]">
        {programs.map((program) => (
          <SelectItem key={program.id} value={program.id} className="py-2">
            <span className="flex items-center gap-2.5">
              <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded">
                <BrandLogo
                  src={program.brandProfileImage}
                  name={program.companyName}
                  className="size-6 rounded"
                  initialsClassName="text-[10px]"
                />
              </span>
              <span className="truncate">{program.companyName}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
